"use client";

import { ArrowUpRight, Check, MousePointer2, Route, Sparkles, Target } from "lucide-react";
import { useState } from "react";
import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { CcSectionReveal } from "@/components/ui/CcSectionReveal";
import type { WebdesignExperienceCopy } from "./webdesign-experience-copy";
import styles from "./webdesign-experience.module.scss";

type Copy = WebdesignExperienceCopy;
const icons = [Sparkles, Route, Target];

export function CcWebdesignExperience({ copy }: { copy: Copy }) {
  const [active, setActive] = useState(0);
  const layer = copy.layers[active];
  return <CcSectionReveal className={styles.section} data-cc-nav="light" aria-labelledby="webdesign-experience-title">
    <div className={`cc-container ${styles.layout}`}>
      <div className={styles.stage} data-layer={layer.key} role="img" aria-label={copy.previewLabel}>
        <div className={styles.grid} />
        <div className={styles.orbit} /><div className={styles.orbitSmall} />
        <div className={styles.browser} aria-hidden="true">
          <div className={styles.chrome}><span className={styles.dots}><i/><i/><i/></span><span>{copy.demo.url}</span><span className={styles.statusDot}/></div>
          <div className={styles.website}>
            <div className={styles.nav}><strong>{copy.demo.brand}</strong><span>{copy.demo.nav}</span><ArrowUpRight size={16}/></div>
            <div className={styles.mockHero}>
              <div className={styles.heroCopy}><span>{copy.demo.eyebrow}</span><h3>{copy.demo.title}</h3><div className={styles.mockButton}>{copy.demo.button}<ArrowUpRight size={15}/><span className={styles.buttonRipple}/></div></div>
              <div className={styles.sculpture}><div className={styles.arch}/><div className={styles.sphere}/><div className={styles.plinth}/><div className={styles.shadow}/></div>
            </div>
            <div className={styles.mockFooter}><span>{copy.demo.caption}</span><span className={styles.line}/><span className={styles.swatches}><i/><i/><i/></span></div>
            <div className={styles.guides}><i/><i/><i/></div>
            <svg className={styles.journey} viewBox="0 0 500 320" preserveAspectRatio="none"><path d="M420 30 C 475 80, 100 65, 100 160 S 90 265, 155 235"/><circle r="5"><animateMotion dur="4s" repeatCount="indefinite" path="M420 30 C 475 80, 100 65, 100 160 S 90 265, 155 235"/></circle></svg>
          </div>
        </div>
        <div className={styles.signal} aria-hidden="true"><span><Check size={14}/></span><div key={layer.key}>{layer.signal}</div></div>
        <div className={styles.cursor} aria-hidden="true"><MousePointer2 size={26} fill="currentColor"/><span>{copy.demo.brand}</span></div>
        <div className={styles.colorStrip} aria-hidden="true"><i/><i/><i/><i/><i/></div>
      </div>
      <div className={styles.copy}>
        <CcSanjayaEyebrow>{copy.eyebrow}</CcSanjayaEyebrow>
        <h2 id="webdesign-experience-title">{copy.title}</h2><p className={styles.lead}>{copy.lead}</p>
        <div className={styles.layers} role="group" aria-label={copy.controlLabel}>
          {copy.layers.map((item, index) => { const Icon = icons[index]; return <button key={item.key} type="button" className={styles.layer} aria-pressed={active === index} onClick={() => setActive(index)} onMouseEnter={() => setActive(index)} onFocus={() => setActive(index)}>
            <span className={styles.icon}><Icon size={20}/></span><span className={styles.layerCopy}><strong>{item.title}</strong><span>{item.body}</span></span><ArrowUpRight className={styles.arrow} size={18}/>
          </button>; })}
        </div>
        <div className={styles.cta}><CcButton label={copy.cta} bookCallPopup bookCallVariant="default" expand expandCompact/><p>{copy.note}</p></div>
      </div>
    </div>
  </CcSectionReveal>;
}
